import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaCompass, FaHandshake, FaChevronDown } from 'react-icons/fa';

interface NavigationDropdownProps {
  activeView: 'prospection' | 'crm';
  onViewChange: (view: 'prospection' | 'crm') => void;
}

const DropdownContainer = styled.div`
  position: relative;
  display: block; /* Affiché sur tous les écrans */
  margin-right: 16px; /* Espace supplémentaire avant les icônes */
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px; /* Taille desktop confortable */
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  justify-content: space-between;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    border-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  /* Plus compact sur mobile */
  @media (max-width: 768px) {
    gap: 6px;
    padding: 6px 12px;
    font-size: 13px;
    min-width: 120px;
    border-radius: 6px;
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px); /* Desktop */
  left: 0;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 2000; /* Augmenté pour être au-dessus de la barre de recherche */
  min-width: 160px; /* Desktop */
  overflow: hidden;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  
  /* Plus compact sur mobile */
  @media (max-width: 768px) {
    top: calc(100% + 6px);
    min-width: 140px;
    border-radius: 6px;
  }
`;

const DropdownItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px; /* Desktop */
  width: 100%;
  padding: 12px 16px; /* Desktop */
  background-color: ${({ $active, theme }) => $active ? theme.colors.primaryLight : 'white'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text};
  border: none;
  font-size: 14px; /* Desktop */
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryLight};
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:active {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
  
  /* Icônes */
  svg {
    font-size: 16px; /* Desktop */
  }
  
  /* Plus compact sur mobile */
  @media (max-width: 768px) {
    gap: 10px;
    padding: 10px 14px;
    font-size: 13px;
    
    svg {
      font-size: 14px;
    }
  }
`;

const DropdownIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropdownText = styled.span`
  flex: 1;
  text-align: left;
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $isOpen }) => $isOpen ? 180 : 0}deg);
`;

export const NavigationDropdown: React.FC<NavigationDropdownProps> = ({
  activeView,
  onViewChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (view: 'prospection' | 'crm') => {
    onViewChange(view);
    setIsOpen(false);
  };

  const getActiveLabel = () => {
    switch (activeView) {
      case 'prospection':
        return 'Prospection';
      case 'crm':
        return 'CRM';
      default:
        return 'Prospection';
    }
  };

  const getActiveIcon = () => {
    switch (activeView) {
      case 'prospection':
        return <FaCompass />;
      case 'crm':
        return <FaHandshake />;
      default:
        return <FaCompass />;
    }
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton 
        onClick={handleToggle}
        aria-label={`Navigation: ${getActiveLabel()}`}
        aria-expanded={isOpen}
      >
        <DropdownIcon>
          {getActiveIcon()}
        </DropdownIcon>
        <DropdownText>{getActiveLabel()}</DropdownText>
        <ChevronIcon $isOpen={isOpen}>
          <FaChevronDown size={12} />
        </ChevronIcon>
      </DropdownButton>
      
      <DropdownMenu $isOpen={isOpen}>
        <DropdownItem 
          onClick={() => handleSelect('prospection')}
          $active={activeView === 'prospection'}
        >
          <FaCompass />
          <span>Prospection</span>
        </DropdownItem>
        
        <DropdownItem 
          onClick={() => handleSelect('crm')}
          $active={activeView === 'crm'}
        >
          <FaHandshake />
          <span>CRM</span>
        </DropdownItem>
      </DropdownMenu>
    </DropdownContainer>
  );
};
